import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuPlus, LuShieldCheck } from "react-icons/lu";
import { FiSearch, FiUsers } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import UserTable from "./UserTable";
import ReactModal from "../ReactModal";
import UserDetails from "./UserDetails";
import { useGetUsersQuery } from "../../store/services/userServices";
import { isAdmin } from "../../utils/roleUtils";
import { userDetailsActions } from "../../store/userDetailsSlice";

const PAGE_LIMIT = 10;

/* Citizens (the app's registered users) and ATK's internal staff share this page.
   `type` is sent to GET /admin/users ("internal" = staff, anything else = citizens) so the API returns only that group. */
const USER_TYPE_META = {
  citizen: {
    path: "/users",
    eyebrow: "Qytetarët",
    title: "Përdoruesit qytetarë",
    countLabel: "qytetarë të regjistruar",
    listLabel: "Lista e qytetarëve të regjistruar",
    emptySubtitle: "Qytetarët e regjistruar do të shfaqen këtu.",
    icon: FiUsers,
  },
  internal: {
    path: "/atk_users",
    eyebrow: "Stafi i ATK-së",
    title: "Përdoruesit e ATK-së",
    countLabel: "përdorues të brendshëm",
    listLabel: "Lista e përdoruesve të brendshëm të ATK-së",
    emptySubtitle: "Përdoruesit e brendshëm të ATK-së do të shfaqen këtu.",
    icon: LuShieldCheck,
  },
};

export default function UserDashboard({ userType = "citizen" }) {
  const meta = USER_TYPE_META[userType] || USER_TYPE_META.citizen;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isUserDetailsOpen = useSelector((state) => state.user_details.isOpen);

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState({});
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));

  const { data, isLoading, isFetching } = useGetUsersQuery({ ...params, type: userType === "internal" ? "internal" : "citizen" });

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => {
      if (k !== "type") newParams[k] = v;
    });
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredUsers(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const urlParams = new URLSearchParams(params).toString();
    navigate(`${meta.path}?${urlParams}`, { replace: true });
  }, [params, navigate, meta.path]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (user) => {
    dispatch(userDetailsActions.openModal(user));
  };

  const hasFilters = !!(params.q || params.role);

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty = !isLoading && !isFetching && filteredUsers.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      <ReactModal />
      {isUserDetailsOpen && <UserDetails />}

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <meta.icon size={18} />, label: meta.eyebrow }}
          title={meta.title}
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} ${meta.countLabel}`
              : meta.listLabel
          }
          actions={
            userType === "internal" && isAdmin() && (
              <button
                type="button"
                onClick={() => navigate("/users/add")}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
              >
                <LuPlus size={18} />
                Krijo përdorues
              </button>
            )
          }
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas emrit, email-it ose telefonit..."
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : meta.icon}
            title={hasFilters ? "Asnjë përdorues nuk u gjet" : "Ende asnjë përdorues"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : meta.emptySubtitle
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <UserTable
            users={filteredUsers}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
            userType={userType}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
}
